import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CtaBanner from '../components/home/CtaBanner';

const principles = [
  ['Understand the work first', 'We start with the customer, workflow, constraint, and business result before recommending a stack.'],
  ['Make the scope visible', 'The deliverables, timeline, cost, exclusions, and recurring services belong in writing.'],
  ['Use tools responsibly', 'AI can accelerate research, development, and testing. It does not replace review, judgment, or accountability.'],
  ['Build for ownership', 'Clear handoff, understandable systems, and client-owned accounts are part of a healthy project.'],
];

const About = () => (
  <>
    <Helmet>
      <title>About Nexgeius | Toronto Development Team</title>
      <meta name="description" content="Nexgeius is a Toronto-based development team focused on custom websites, business software, and practical AI automation." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://www.nexgeius.com/about" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="About Nexgeius | Toronto Development Team" />
      <meta property="og:description" content="A small, direct team building useful digital systems with clear scope and ownership." />
      <meta property="og:url" content="https://www.nexgeius.com/about" />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Nexgeius',
        url: 'https://www.nexgeius.com/',
        description: 'Toronto-based development team focused on custom websites, business software, and practical AI automation.',
        email: 'nexgeius@gmail.com',
        address: { '@type': 'PostalAddress', addressLocality: 'Toronto', addressRegion: 'ON', addressCountry: 'CA' },
      })}</script>
    </Helmet>

    <main>
      <section className="page-grid bg-[#F6F8FA] px-6 pb-20 pt-32 lg:px-8 lg:pb-24">
        <div className="relative z-10 mx-auto grid max-w-7xl gap-9 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
          <div>
            <p className="section-kicker">About Nexgeius</p>
            <h1 className="mt-5 max-w-4xl text-[clamp(2.8rem,6vw,5.8rem)] font-semibold leading-[0.96] tracking-[-0.055em] text-[#102A3A]">A small Toronto team with a practical way of building.</h1>
          </div>
          <div className="max-w-xl lg:justify-self-end">
            <p className="text-lg leading-8 text-[#52636E]">We help businesses become easier to find, easier to run, and better prepared to grow through websites, software, and thoughtful automation.</p>
            <Link to="/case-studies" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#176B8A]">See what we have built <span aria-hidden="true">&rarr;</span></Link>
          </div>
        </div>
      </section>

      <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.7fr_1.3fr]">
          <div>
            <p className="section-kicker">Why we exist</p>
            <h2 className="mt-4 max-w-md text-4xl font-semibold leading-[1.04] tracking-[-0.045em] text-[#102A3A]">Good software should make the business feel simpler.</h2>
          </div>
          <div className="max-w-3xl space-y-5 text-lg leading-8 text-[#52636E]">
            <p>Nexgeius grew from a team with university roots and a shared interest in building useful digital products. The work began with local businesses and expanded across legal services, operations, SaaS, sports data, and commerce.</p>
            <p>That range taught us the same lesson repeatedly: technology is only valuable when it clarifies the customer journey or removes friction from the way people work. The best solution is often smaller, more focused, and easier to own than the first idea.</p>
            <p>We keep the team direct, the scope written, and the communication close to the people doing the work.</p>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F8FA] px-6 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[.7fr_1.3fr]">
            <div>
              <p className="section-kicker">How we operate</p>
              <h2 className="mt-4 max-w-md text-4xl font-semibold leading-[1.04] tracking-[-0.045em] text-[#102A3A]">The principles behind the process.</h2>
            </div>
            <div className="grid gap-px overflow-hidden rounded-2xl border border-[#DCE4E8] bg-[#DCE4E8] sm:grid-cols-2">
              {principles.map(([title, copy], index) => (
                <article key={title} className="bg-white p-7 md:p-8">
                  <span className="text-xs font-semibold text-[#176B8A]">0{index + 1}</span>
                  <h3 className="mt-8 text-xl font-semibold tracking-[-0.025em] text-[#102A3A]">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#63747E]">{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-6 py-16 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 rounded-[1.75rem] border border-[#D5E0E5] bg-[#FBFCFD] p-8 md:grid-cols-[1fr_auto] md:items-center md:p-10">
          <div>
            <p className="text-sm font-semibold text-[#176B8A]">Based in Toronto. Working across Canada.</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Need a direct technical partner?</h2>
          </div>
          <Link to="/contact" className="button-primary inline-flex items-center gap-2 md:justify-self-end">Talk to the team <span aria-hidden="true">&rarr;</span></Link>
        </div>
      </section>

      <CtaBanner />
    </main>
  </>
);

export default About;
